import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { NoNetworkError } from '@confluence/network';
import { causedByAbortError } from '@confluence/network';

import type { JiraProject, JiraProjects } from '../__types__/apiUtils';

import { fetchJiraProjects } from './apiUtils';

export const isValidProjectFromLocalStorageQuery = async (
	localStorageProject: JiraProject,
	selectedAvailableSite: AvailableSite,
	currentAbortController: AbortController | null,
): Promise<{ isAvailable: boolean; searchResults: JiraProjects }> => {
	try {
		// if not available in the available projects, we should check via the API, like we do with projectSelectorQuery
		const cloudId = selectedAvailableSite.cloudId;
		const query = localStorageProject.name;

		const searchResults = await fetchJiraProjects({
			cloudId,
			orderBy: 'name',
			query,
			signal: currentAbortController?.signal,
		});

		const isAvailable = !!searchResults?.some(
			(project) =>
				project.id === localStorageProject.id &&
				project.key === localStorageProject.key &&
				project.name === localStorageProject.name,
		);

		return { isAvailable, searchResults: searchResults || [] };
	} catch (error) {
		// If API call fails, treat as invalid to be safe
		// unless it's an abort error, in which case we should handle it gracefully
		if (causedByAbortError(error as NoNetworkError)) {
			// For abort errors, we can't determine validity, so return false
			return { isAvailable: false, searchResults: [] };
		}
		// For other errors, log and return false to be safe
		// eslint-disable-next-line no-console
		console.error('Error validating project:', error); // TODO: Remove this console.log
		return { isAvailable: false, searchResults: [] };
	}
};

export const isValidProject = async (
	localStorageProject: JiraProject,
	availableProjects: JiraProjects,
): Promise<boolean> => {
	return availableProjects.some(
		(project) =>
			project.id === localStorageProject.id &&
			project.key === localStorageProject.key &&
			project.name === localStorageProject.name,
	);
};
